/**
 * The Ledger leaf (TMMPDM-0095, TMMAPI-0162 U3 reads and U4 actions): a partner's balance and open
 * holds, its statements, one statement's lines (`?statement=`), and its unassigned lines; and the
 * admin actions. Shared by the partner page (a reseller) and the affiliate page.
 *
 * Every status, hold and amount is the server's, shown as sent (the composition rule): the console
 * adds nothing up and judges nothing. Amounts are the partner's own (MM-D-140), in the payee's
 * currency, negatives red and in parentheses.
 *
 * Before tmm-api deploys the ledger every route answers 404: the leaf says "isn't available yet",
 * neutrally, shows no actions, and never retries; the next visit reads again. A partner whose payout
 * account has no pay-api payee (0161's `taxIdStatus` is `null`) has no ledger yet.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
  Box,
  Button,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { EmptyState, ErrorState, SectionCard, useNotify } from '@/components/page';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { stickyTableContainerSx } from '@/components/ui';
import { adminApi } from '@/api/tmm/admin';
import { ApiError } from '@/api/errors';
import { formatDate } from '@/format';
import type {
  LedgerHoldView,
  LedgerLinePage,
  LedgerLineView,
  LedgerStatementView,
  PartnerLedgerView,
} from '@/api/tmm/types';
import { HoldKindChip, LedgerMoney, LedgerStatusChip } from './ledgerShared';
import { isNotDeployed, lineKindLabel, periodLabel } from './ledger';
import {
  AdjustmentDialog,
  HoldForCauseDialog,
  ManualPaymentDialog,
  ReleaseLedgerHoldDialog,
  VoidLedgerHoldDialog,
} from './LedgerActionDialogs';

type Load<T> =
  | { state: 'loading' }
  | { state: 'ready'; data: T }
  | { state: 'notDeployed' }
  | { state: 'failed'; failure: unknown };

/** The payee, as the partner's agreement says (0161): `undefined` while it is read. */
export type PayeeState =
  | { state: 'loading' }
  | { state: 'payee'; payeeRef: string }
  | { state: 'none' }
  | { state: 'failed'; failure: unknown; onRetry: () => void };

/** Which slice of the ledger this visit shows. `all` is the affiliate page's Ledger tab. */
export type LedgerPart = 'all' | 'balance' | 'statements' | 'unassigned';

interface PartnerLedgerPanelProps {
  payee: PayeeState;
  /** The partner's name, as the page shows it (void's typed confirmation names it). */
  name: string;
  /**
   * Where the partner's payee status (0161's onboarding) is shown: a route, or `#id` for a section on
   * the same page (a plain anchor, so the address keeps its query and the browser scrolls to it).
   */
  payeeStatusTo: string;
  /** The heading level of the leaf's own sections. */
  headingLevel?: 'h2' | 'h3';
  /** Default `all`: balance, statements and unassigned lines together. */
  part?: LedgerPart;
}

export function PartnerLedgerPanel({
  payee,
  name,
  payeeStatusTo,
  headingLevel = 'h2',
  part = 'all',
}: PartnerLedgerPanelProps) {
  const { t } = useCopy();
  if (payee.state === 'loading') return <LoadingSpinner />;
  if (payee.state === 'failed') {
    return (
      <ErrorState
        error={payee.failure}
        message={t('ledger.payeeLoadError', "Couldn't read this partner's payee.")}
        onRetry={payee.onRetry}
      />
    );
  }
  if (payee.state === 'none') return <NoPayee payeeStatusTo={payeeStatusTo} />;
  return (
    <LedgerView
      key={payee.payeeRef}
      payeeRef={payee.payeeRef}
      name={name}
      payeeStatusTo={payeeStatusTo}
      headingLevel={headingLevel}
      part={part}
    />
  );
}

/** Reads one ledger route into a `Load`; a 404 is "not deployed". */
function useLedgerRead<T>(read: (() => Promise<T>) | null, token: number): Load<T> {
  const [load, setLoad] = useState<{ token: number; value: Load<T> } | null>(null);
  useEffect(() => {
    if (read === null) return;
    let cancelled = false;
    read()
      .then((data) => {
        if (!cancelled) setLoad({ token, value: { state: 'ready', data } });
      })
      .catch((failure: unknown) => {
        if (cancelled) return;
        setLoad({
          token,
          value: isNotDeployed(failure) ? { state: 'notDeployed' } : { state: 'failed', failure },
        });
      });
    return () => {
      cancelled = true;
    };
  }, [read, token]);
  return load?.token === token ? load.value : { state: 'loading' };
}

type Dialog =
  | { kind: 'hold' }
  | { kind: 'release'; hold: LedgerHoldView }
  | { kind: 'void'; hold: LedgerHoldView }
  | { kind: 'payment' }
  | { kind: 'adjustment' }
  | null;

function LedgerView({
  payeeRef,
  name,
  payeeStatusTo,
  headingLevel,
  part,
}: {
  payeeRef: string;
  name: string;
  payeeStatusTo: string;
  headingLevel: 'h2' | 'h3';
  part: LedgerPart;
}) {
  const { t } = useCopy();
  const notify = useNotify();
  const [searchParams, setSearchParams] = useSearchParams();
  const openStatementId = searchParams.get('statement');
  const [token, setToken] = useState(0);
  const [dialog, setDialog] = useState<Dialog>(null);
  const reload = useCallback(() => setToken((n) => n + 1), []);

  const readSummary = useCallback(() => adminApi.getPartnerLedger(payeeRef), [payeeRef]);
  const summary = useLedgerRead<PartnerLedgerView>(readSummary, token);
  const showBalance = part === 'all' || part === 'balance';
  const showStatements = part === 'all' || part === 'statements';
  const showUnassigned = part === 'all' || part === 'unassigned';
  const statements = useStatements(
    payeeRef,
    summary.state === 'ready' && (showBalance || showStatements),
    token,
  );

  const openStatement = (statementId: string | null) => {
    const params = new URLSearchParams(searchParams);
    if (statementId === null) params.delete('statement');
    else {
      params.set('statement', statementId);
      // Balance and holds hands the open statement to the Statements leaf.
      if (part === 'balance') params.set('tab', 'ledgerStatements');
    }
    setSearchParams(params);
  };

  const done = (message: string) => {
    setDialog(null);
    notify.success(message);
    reload();
  };

  if (summary.state === 'loading') return <LoadingSpinner />;
  if (summary.state === 'notDeployed') return <NotAvailable />;
  if (summary.state === 'failed') {
    const noPayee =
      summary.failure instanceof ApiError && String(summary.failure.code) === 'PAYEE_NOT_FOUND';
    if (noPayee) return <NoPayee payeeStatusTo={payeeStatusTo} />;
    return (
      <ErrorState
        error={summary.failure}
        message={t('ledger.loadError', "Couldn't load the ledger.")}
        onRetry={reload}
      />
    );
  }

  const ledger = summary.data;
  const holds = Array.isArray(ledger.openHolds) ? ledger.openHolds : [];
  // The summary's own list of open holds: Hold for cause is offered while none of them is FOR_CAUSE.
  const forCauseOpen = holds.some((hold) => hold.kind === 'FOR_CAUSE');
  const latest = ledger.lastStatement ?? null;
  const subheading = headingLevel === 'h2' ? 'h3' : 'h4';

  return (
    <Stack spacing={2}>
      {showBalance ? (
        <SectionCard
          title={t('ledger.summary.title', 'Balance and holds')}
          headingLevel={headingLevel}
          id="ledger-summary"
        >
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', mb: 2 }}>
            {forCauseOpen ? null : (
              <Button variant="outlined" onClick={() => setDialog({ kind: 'hold' })}>
                {t('ledger.hold.action', 'Hold for cause…')}
              </Button>
            )}
            {latest !== null ? (
              <Button variant="outlined" onClick={() => setDialog({ kind: 'payment' })}>
                {t('ledger.payment.action', 'Record a manual payment…')}
              </Button>
            ) : null}
            <Button variant="outlined" onClick={() => setDialog({ kind: 'adjustment' })}>
              {t('ledger.adjustment.action', 'Post an adjustment…')}
            </Button>
          </Stack>
          <Typography variant="body1">
            {t('ledger.summary.balance', 'Balance:')}{' '}
            <LedgerMoney minor={ledger.balanceMinor} currency={ledger.currency} emphasis />
          </Typography>
          <Typography variant="subtitle1" component={subheading} sx={{ mt: 2, mb: 1 }}>
            {t('ledger.holds.title', 'Open holds')}
          </Typography>
          {holds.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('ledger.holds.none', 'No open holds.')}
            </Typography>
          ) : (
            <Stack component="ul" spacing={1} sx={{ listStyle: 'none', p: 0, m: 0 }}>
              {holds.map((hold, index) => (
                <HoldRow
                  key={hold.holdId ?? `hold-${index}`}
                  hold={hold}
                  currency={ledger.currency}
                  onRelease={() => setDialog({ kind: 'release', hold })}
                  onVoid={() => setDialog({ kind: 'void', hold })}
                />
              ))}
            </Stack>
          )}
          <Typography variant="subtitle1" component={subheading} sx={{ mt: 2, mb: 1 }}>
            {t('ledger.last.title', 'Last statement')}
          </Typography>
          {latest === null ? (
            <Typography variant="body2" color="text.secondary">
              {t('ledger.last.none', 'No statement has closed yet.')}
            </Typography>
          ) : (
            <LastStatement
              statement={latest}
              onOpen={() => {
                if (latest.statementId !== undefined) openStatement(latest.statementId);
              }}
            />
          )}
        </SectionCard>
      ) : null}

      {showStatements && openStatementId !== null ? (
        <StatementDetail
          key={`${openStatementId}:${token}`}
          payeeRef={payeeRef}
          statementId={openStatementId}
          headingLevel={headingLevel}
          onClose={() => openStatement(null)}
        />
      ) : null}

      {showStatements ? (
        <SectionCard
          title={t('ledger.statements.title', 'Statements')}
          headingLevel={headingLevel}
          id="ledger-statements"
        >
          <StatementsTable
            load={statements}
            openStatementId={openStatementId}
            onOpen={(id) => openStatement(id)}
          />
        </SectionCard>
      ) : null}

      {showUnassigned ? (
        <SectionCard
          title={t('ledger.unassigned.title', 'Unassigned lines')}
          subtitle={t(
            'ledger.unassigned.help',
            'Lines no statement has taken yet. The next statement takes them.',
          )}
          headingLevel={headingLevel}
          id="ledger-unassigned"
        >
          <PagedLines
            key={`unassigned:${token}`}
            read={(afterId) => adminApi.listUnassignedLedgerLines(payeeRef, afterId)}
            label={t('ledger.unassigned.title', 'Unassigned lines')}
            empty={t('ledger.unassigned.empty', 'No unassigned lines.')}
          />
        </SectionCard>
      ) : null}

      {showBalance ? (
        <>
          <HoldForCauseDialog
            open={dialog?.kind === 'hold'}
            payeeRef={payeeRef}
            name={name}
            onClose={() => setDialog(null)}
            onDone={() => done(t('ledger.hold.done', 'Payouts held for cause.'))}
          />
          <ReleaseLedgerHoldDialog
            hold={dialog?.kind === 'release' ? dialog.hold : null}
            payeeRef={payeeRef}
            name={name}
            onClose={() => setDialog(null)}
            onDone={() => done(t('ledger.release.done', 'Hold released.'))}
          />
          <VoidLedgerHoldDialog
            hold={dialog?.kind === 'void' ? dialog.hold : null}
            payeeRef={payeeRef}
            name={name}
            onClose={() => setDialog(null)}
            onDone={() => done(t('ledger.void.done', 'Balance voided.'))}
          />
          {latest !== null ? (
            <ManualPaymentDialog
              open={dialog?.kind === 'payment'}
              payeeRef={payeeRef}
              name={name}
              statements={statements.state === 'ready' ? statements.data : []}
              latest={latest}
              balanceMinor={ledger.balanceMinor}
              balanceCurrency={ledger.currency}
              onClose={() => setDialog(null)}
              onDone={() => done(t('ledger.payment.done', 'Manual payment recorded.'))}
            />
          ) : null}
          <AdjustmentDialog
            open={dialog?.kind === 'adjustment'}
            payeeRef={payeeRef}
            name={name}
            currency={ledger.currency}
            onClose={() => setDialog(null)}
            onDone={() => done(t('ledger.adjustment.done', 'Adjustment posted.'))}
          />
        </>
      ) : null}
    </Stack>
  );
}

/** No pay-api payee yet: a link to where 0161's onboarding shows the payee status. */
function NoPayee({ payeeStatusTo }: { payeeStatusTo: string }) {
  const { t } = useCopy();
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="body1" color="text.secondary">
        {t('ledger.noPayee', "This partner isn't set up as a payee yet.")}{' '}
        {payeeStatusTo.startsWith('#') ? (
          <Link href={payeeStatusTo} underline="always">
            {t('ledger.noPayeeLink', 'See the payee status')}
          </Link>
        ) : (
          <Link component={RouterLink} to={payeeStatusTo} underline="always">
            {t('ledger.noPayeeLink', 'See the payee status')}
          </Link>
        )}
      </Typography>
    </Paper>
  );
}

/** The neutral "not available yet" state: tmm-api hasn't deployed the ledger (Step 6). */
function NotAvailable() {
  const { t } = useCopy();
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="body1" color="text.secondary">
        {t('ledger.notAvailable', "The partner ledger isn't available yet.")}
      </Typography>
    </Paper>
  );
}

function HoldRow({
  hold,
  currency,
  onRelease,
  onVoid,
}: {
  hold: LedgerHoldView;
  currency: string | undefined;
  onRelease: () => void;
  onVoid: () => void;
}) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const canAddress = typeof hold.holdId === 'string' && hold.holdId !== '';
  return (
    <Box component="li">
      <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        <HoldKindChip hold={hold} />
        <LedgerMoney minor={hold.amountMinor} currency={currency} />
        {hold.overCap === true ? (
          <Typography variant="body2" component="span">
            {t('ledger.holds.overCap', 'over the cap')}
          </Typography>
        ) : null}
        <Typography variant="body2" component="span" color="text.secondary">
          {t('ledger.holds.since', 'since {{date}}', {
            date: formatDate(hold.openedAt, i18n.language),
          })}
        </Typography>
        <Button size="small" onClick={onRelease} disabled={!canAddress}>
          {t('ledger.release.action', 'Release…')}
        </Button>
        {hold.kind === 'FOR_CAUSE' ? (
          <Button size="small" color="error" onClick={onVoid} disabled={!canAddress}>
            {t('ledger.void.action', 'Void…')}
          </Button>
        ) : null}
      </Stack>
      {hold.reason ? (
        <Typography variant="body2" color="text.secondary">
          {hold.reason}
        </Typography>
      ) : null}
    </Box>
  );
}

function LastStatement({
  statement,
  onOpen,
}: {
  statement: LedgerStatementView;
  onOpen: () => void;
}) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  return (
    <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
      <Typography variant="body1" component="span">
        {periodLabel(statement.periodMonth, i18n.language)}
      </Typography>
      {statement.status !== undefined && statement.status !== '' ? (
        <LedgerStatusChip status={statement.status} />
      ) : (
        <Typography variant="body2" component="span">
          —
        </Typography>
      )}
      <Typography variant="body2" component="span">
        {t('ledger.statements.col.closing', 'Closing')}{' '}
        <LedgerMoney minor={statement.closingMinor} currency={statement.currency} />
      </Typography>
      <Button size="small" onClick={onOpen} disabled={statement.statementId === undefined}>
        {t('ledger.statements.open', 'Open statement')}
      </Button>
    </Stack>
  );
}

/** The statements, newest first, every page the person asked for. */
function useStatements(
  payeeRef: string,
  enabled: boolean,
  token: number,
): Load<LedgerStatementView[]> & {
  hasMore?: boolean;
  loadMore?: () => void;
  loadingMore?: boolean;
} {
  const [rows, setRows] = useState<{
    token: number;
    load: Load<LedgerStatementView[]>;
    nextAfterId?: string | undefined;
    hasMore: boolean;
  } | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    adminApi
      .listLedgerStatements(payeeRef)
      .then((page) => {
        if (cancelled) return;
        setRows({
          token,
          load: { state: 'ready', data: Array.isArray(page.statements) ? page.statements : [] },
          nextAfterId: page.nextAfterId,
          hasMore: page.hasMore === true,
        });
      })
      .catch((failure: unknown) => {
        if (cancelled) return;
        setRows({
          token,
          load: isNotDeployed(failure) ? { state: 'notDeployed' } : { state: 'failed', failure },
          hasMore: false,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [payeeRef, enabled, token]);

  if (rows === null || rows.token !== token) return { state: 'loading' };
  if (rows.load.state !== 'ready' || !rows.hasMore || rows.nextAfterId === undefined) {
    return rows.load;
  }
  const loaded = rows.load.data;
  const afterId = rows.nextAfterId;
  return {
    ...rows.load,
    hasMore: true,
    loadingMore,
    loadMore: () => {
      setLoadingMore(true);
      adminApi
        .listLedgerStatements(payeeRef, afterId)
        .then((page) => {
          setRows({
            token,
            load: { state: 'ready', data: [...loaded, ...(page.statements ?? [])] },
            nextAfterId: page.nextAfterId,
            hasMore: page.hasMore === true,
          });
        })
        .catch(() => {
          // The page already shown stays; Load more is offered again.
        })
        .finally(() => setLoadingMore(false));
    },
  };
}

function StatementsTable({
  load,
  openStatementId,
  onOpen,
}: {
  load: ReturnType<typeof useStatements>;
  openStatementId: string | null;
  onOpen: (statementId: string) => void;
}) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  if (load.state === 'loading') return <LoadingSpinner />;
  if (load.state === 'notDeployed') return <NotAvailable />;
  if (load.state === 'failed') {
    return (
      <ErrorState
        error={load.failure}
        message={t('ledger.statements.loadError', "Couldn't load the statements.")}
      />
    );
  }
  const rows = load.data;
  return (
    <>
      <TableContainer component={Paper} variant="outlined" sx={stickyTableContainerSx}>
        <Table size="small" stickyHeader aria-label={t('ledger.statements.title', 'Statements')}>
          <TableHead>
            <TableRow>
              <TableCell>{t('ledger.statements.col.period', 'Period')}</TableCell>
              <TableCell>{t('ledger.statements.col.status', 'Status')}</TableCell>
              <TableCell align="right">{t('ledger.statements.col.opening', 'Opening')}</TableCell>
              <TableCell align="right">{t('ledger.statements.col.lines', 'Lines')}</TableCell>
              <TableCell align="right">{t('ledger.statements.col.closing', 'Closing')}</TableCell>
              <TableCell>{t('ledger.statements.col.paid', 'Paid on')}</TableCell>
              <TableCell>{t('ledger.statements.col.actions', 'Actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <EmptyState title={t('ledger.statements.empty', 'No statements yet.')} />
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => {
                const period = periodLabel(row.periodMonth, locale);
                const open = row.statementId !== undefined && row.statementId === openStatementId;
                return (
                  <TableRow key={row.statementId ?? `statement-${index}`} selected={open}>
                    <TableCell>{period}</TableCell>
                    <TableCell>
                      {row.status !== undefined && row.status !== '' ? (
                        <LedgerStatusChip status={row.status} />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <LedgerMoney minor={row.openingMinor} currency={row.currency} />
                    </TableCell>
                    <TableCell align="right">
                      <LedgerMoney minor={row.linesMinor} currency={row.currency} />
                    </TableCell>
                    <TableCell align="right">
                      <LedgerMoney minor={row.closingMinor} currency={row.currency} />
                    </TableCell>
                    <TableCell>{row.paidOn ? formatDate(row.paidOn, locale) : '—'}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        disabled={row.statementId === undefined}
                        onClick={() => {
                          if (row.statementId !== undefined) onOpen(row.statementId);
                        }}
                        aria-label={t(
                          'ledger.statements.openRow',
                          'Open the {{period}} statement',
                          {
                            period,
                          },
                        )}
                        {...(open ? { 'aria-current': 'true' as const } : {})}
                      >
                        {t('ledger.statements.open', 'Open statement')}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {load.hasMore && load.loadMore ? (
        <Box sx={{ mt: 1 }}>
          <Button onClick={load.loadMore} disabled={load.loadingMore === true}>
            {t('ledger.loadMore', 'Load more')}
          </Button>
        </Box>
      ) : null}
    </>
  );
}

function StatementDetail({
  payeeRef,
  statementId,
  headingLevel,
  onClose,
}: {
  payeeRef: string;
  statementId: string;
  headingLevel: 'h2' | 'h3';
  onClose: () => void;
}) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const read = useCallback(
    () => adminApi.getLedgerStatement(payeeRef, statementId),
    [payeeRef, statementId],
  );
  const load = useLedgerRead(read, 0);
  const title =
    load.state === 'ready'
      ? t('ledger.statement.title', 'Statement for {{period}}', {
          period: periodLabel(load.data.periodMonth, locale),
        })
      : t('ledger.statement.titleLoading', 'Statement');
  let body: ReactNode;
  if (load.state === 'loading') body = <LoadingSpinner />;
  else if (
    load.state === 'notDeployed' ||
    (load.state === 'failed' &&
      load.failure instanceof ApiError &&
      String(load.failure.code) === 'STATEMENT_NOT_FOUND')
  ) {
    body = (
      <Typography variant="body1" color="text.secondary">
        {t('ledger.statement.notFound', "This statement isn't available.")}
      </Typography>
    );
  } else if (load.state === 'failed') {
    body = (
      <ErrorState
        error={load.failure}
        message={t('ledger.statement.loadError', "Couldn't load the statement.")}
      />
    );
  } else {
    const s = load.data;
    body = (
      <>
        <Stack
          direction="row"
          spacing={2}
          useFlexGap
          sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 2 }}
        >
          {s.status !== undefined && s.status !== '' ? (
            <LedgerStatusChip status={s.status} />
          ) : (
            <Typography variant="body2" component="span">
              —
            </Typography>
          )}
          <Typography variant="body2" component="span">
            {t('ledger.statements.col.opening', 'Opening')}{' '}
            <LedgerMoney minor={s.openingMinor} currency={s.currency} />
          </Typography>
          <Typography variant="body2" component="span">
            {t('ledger.statements.col.closing', 'Closing')}{' '}
            <LedgerMoney minor={s.closingMinor} currency={s.currency} />
          </Typography>
          {s.paidOn ? (
            <Typography variant="body2" component="span">
              {t('ledger.statement.paidOn', 'Paid on {{date}}', {
                date: formatDate(s.paidOn, locale),
              })}
            </Typography>
          ) : null}
        </Stack>
        <PagedLines
          read={(afterId) => adminApi.listLedgerStatementLines(payeeRef, statementId, afterId)}
          label={title}
          empty={t('ledger.statement.noLines', 'No lines on this statement.')}
        />
      </>
    );
  }
  return (
    <SectionCard
      title={title}
      headingLevel={headingLevel}
      id="ledger-statement"
      actions={
        <Button size="small" onClick={onClose}>
          {t('ledger.statement.close', 'Close statement')}
        </Button>
      }
    >
      {body}
    </SectionCard>
  );
}

/** A paged table of lines: the first page, then every page the person asked for. */
function PagedLines({
  read,
  label,
  empty,
}: {
  read: (afterId?: string) => Promise<LedgerLinePage>;
  label: string;
  empty: string;
}) {
  const { t } = useCopy();
  const [pages, setPages] = useState<{
    lines: LedgerLineView[];
    nextAfterId?: string | undefined;
    hasMore: boolean;
  } | null>(null);
  const [failure, setFailure] = useState<{ notDeployed: boolean; cause: unknown } | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  // The first page is read once per mount (the caller's key re-mounts it after an action).
  const [first] = useState(() => read);
  useEffect(() => {
    let cancelled = false;
    first()
      .then((page) => {
        if (cancelled) return;
        setPages({
          lines: Array.isArray(page.lines) ? page.lines : [],
          nextAfterId: page.nextAfterId,
          hasMore: page.hasMore === true,
        });
      })
      .catch((cause: unknown) => {
        if (!cancelled) setFailure({ notDeployed: isNotDeployed(cause), cause });
      });
    return () => {
      cancelled = true;
    };
  }, [first]);

  if (failure?.notDeployed) return <NotAvailable />;
  if (failure) {
    return (
      <ErrorState
        error={failure.cause}
        message={t('ledger.lines.loadError', "Couldn't load the lines.")}
      />
    );
  }
  if (pages === null) return <LoadingSpinner />;
  const more = () => {
    if (pages.nextAfterId === undefined) return;
    setLoadingMore(true);
    read(pages.nextAfterId)
      .then((page) =>
        setPages({
          lines: [...pages.lines, ...(page.lines ?? [])],
          nextAfterId: page.nextAfterId,
          hasMore: page.hasMore === true,
        }),
      )
      .catch(() => {
        // The lines already shown stay; Load more is offered again.
      })
      .finally(() => setLoadingMore(false));
  };
  return (
    <>
      <LedgerLinesTable lines={pages.lines} label={label} empty={empty} />
      {pages.hasMore && pages.nextAfterId !== undefined ? (
        <Box sx={{ mt: 1 }}>
          <Button onClick={more} disabled={loadingMore}>
            {t('ledger.loadMore', 'Load more')}
          </Button>
        </Box>
      ) : null}
    </>
  );
}

/** Lines: date, kind in words, description or reason, the reversal link, and the signed amount. */
export function LedgerLinesTable({
  lines,
  label,
  empty,
}: {
  lines: LedgerLineView[];
  label: string;
  empty: string;
}) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  return (
    <TableContainer component={Paper} variant="outlined" sx={stickyTableContainerSx}>
      <Table size="small" stickyHeader aria-label={label}>
        <TableHead>
          <TableRow>
            <TableCell>{t('ledger.lines.col.date', 'Date')}</TableCell>
            <TableCell>{t('ledger.lines.col.kind', 'Kind')}</TableCell>
            <TableCell>{t('ledger.lines.col.description', 'Description')}</TableCell>
            <TableCell align="right">{t('ledger.lines.col.amount', 'Amount')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {lines.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4}>
                <EmptyState title={empty} />
              </TableCell>
            </TableRow>
          ) : (
            lines.map((line, index) => (
              <TableRow key={line.lineId ?? `line-${index}`}>
                <TableCell>{formatDate(line.createdAt, locale)}</TableCell>
                <TableCell>{lineKindLabel(line.kind, t)}</TableCell>
                <TableCell>
                  {line.description ?? line.reason ?? '—'}
                  {line.reversesLineId ? (
                    <Typography variant="caption" color="text.secondary" component="div">
                      {t('ledger.lines.reverses', 'Reverses line {{id}}', {
                        id: line.reversesLineId,
                      })}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell align="right">
                  <LedgerMoney minor={line.amountMinor} currency={line.currency} />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
